import { ApolloQueryResult, NetworkStatus } from '@apollo/client';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Flex,
  LoadingView
} from '@payflip/ui/v2';
import { ReactNode } from 'react';

type LoadingPlaceholderProps = {
  skeleton?: JSX.Element;
  fullScreenLoader?: boolean;
};

function LoadingPlaceholder({
  skeleton,
  fullScreenLoader = false
}: LoadingPlaceholderProps): JSX.Element {
  return skeleton ? (
    skeleton
  ) : (
    <LoadingView
      mt={fullScreenLoader ? 0 : 'space_unit.x4'}
      fixed={fullScreenLoader}
    />
  );
}

type ErrorPlaceholderProps = {
  errorComponent?: JSX.Element;
  errorTitle?: string;
  errorDescription?: string;
};

export function ErrorPlaceholder({
  errorComponent,
  errorTitle,
  errorDescription
}: ErrorPlaceholderProps): JSX.Element {
  return errorComponent ? (
    errorComponent
  ) : (
    <Alert status="error" bgColor="palette_alert.5" p="space_unit.x4">
      <AlertIcon color="palette_alert.50" />
      <Flex flexDirection="column">
        <AlertTitle>{errorTitle ?? 'Oops, something went wrong'}</AlertTitle>
        <AlertDescription>
          {errorDescription ??
            "We encountered an unexpected issue. Please try again in a few moments. If the problem continues, we're here to help in the chat!"}
        </AlertDescription>
      </Flex>
    </Alert>
  );
}

type ApolloProps<TData> =
  | {
      queryResult: ApolloQueryResult<TData>;
      networkStatus?: never;
      children: (data: NonNullable<TData>) => ReactNode;
    }
  | { networkStatus: NetworkStatus; queryResult?: never; children: ReactNode };

type Props<TData> = LoadingPlaceholderProps &
  ErrorPlaceholderProps &
  ApolloProps<TData>;

export function NetworkStatusHandler<TData>({
  queryResult,
  networkStatus,
  skeleton,
  children,
  errorComponent,
  errorTitle,
  errorDescription,
  fullScreenLoader
}: Props<TData>): ReactNode {
  const status = networkStatus ?? queryResult.networkStatus;

  switch (status) {
    /**
     * If the network status is less then 7 (ready) then it is equivalent to data.loading being true.
     * However, exposing those status allow us to adopt specific strategies for them if required.
     */
    case NetworkStatus.loading:
    case NetworkStatus.setVariables:
    case NetworkStatus.fetchMore:
    case NetworkStatus.refetch:
    case NetworkStatus.poll:
      return (
        <LoadingPlaceholder
          skeleton={skeleton}
          fullScreenLoader={fullScreenLoader}
        />
      );
    case NetworkStatus.ready: {
      if (typeof children === 'function') {
        if (queryResult?.data) {
          return children(queryResult.data);
        }
      } else {
        return children;
      }
      return null;
    }
    case NetworkStatus.error:
    default:
      return (
        <ErrorPlaceholder
          errorComponent={errorComponent}
          errorTitle={errorTitle}
          errorDescription={errorDescription}
        />
      );
  }
}

/**
 * NETWORK STATUS:
 *
 * @loading
 * The query has never been run before and the request is now pending.
 * A query will still have this network status even if a result was returned
 * from the cache, but a query was dispatched anyway.
 *
 * @setVariables
 * If a query's variables change and a network request was fired then the network
 * status will be setVariables until the result of that query comes back.
 * React users will see this when options.variables changes on their queries.
 *
 * @fetchMore
 * Indicates that fetchMore was called on this query and that the network request
 * created is currently in flight.
 *
 * @refetch
 * It means that refetch was called on a query and the refetch request is currently in flight.
 *
 * @poll
 * Indicates that a polling query is currently in flight. So for example if you are polling
 * a query every 10 seconds then the network status will switch to poll every 10 seconds
 * whenever a poll request has been sent but not resolved.
 *
 * @ready
 * No request is in flight for this query, and no errors happened. Everything is OK
 *
 * @error
 * No request is in flight for this query, but one or more errors were detected.
 */
